import { FullscreenOverlayContainer, OverlayContainer } from '@angular/cdk/overlay';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, importProvidersFrom, LOCALE_ID, NgModule } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipDefaultOptions } from '@angular/material/tooltip';
import { MapModule } from '@app/core/modules/map.module';
import { firebaseConfig } from '@env/environment';
import { LoaderModule } from '@layout/loader/loader.module';
import { GlobalErrorHandler } from '@services/error-handlers/global.error-handler';
import { JwtInterceptor } from '@services/interceptors/jwt.interceptor';
import { SharedModule } from '@shared/shared.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CoreModule } from './core/modules/core.module';
import { ThemeService } from '@services/theme.service';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import {
  CirclePause,
  Activity,
  Archive,
  ArrowUpRight,
  BatteryCharging,
  Bell,
  Book,
  Calendar,
  Caravan,
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  Cigarette,
  CircleAlert,
  CircleCheck,
  CircleCheckBig,
  CirclePlus,
  CircleStop,
  CircleX,
  ClipboardCheck,
  Clock,
  Clock2,
  Clock9,
  ClockFading,
  Coffee,
  Columns2,
  Copy,
  CornerDownRight,
  Delete,
  DoorOpen,
  Download,
  Droplet,
  Ellipsis,
  EllipsisVertical,
  Eye,
  EyeOff,
  File,
  FileDown,
  FilePlus,
  FileText,
  Filter,
  Flag,
  Fuel,
  Gauge,
  Handshake,
  Headphones,
  HelpCircle,
  Home,
  Hourglass,
  Image,
  Info,
  Layers,
  LifeBuoy,
  Locate,
  LocateFixed,
  Lock,
  LogOut,
  LucideAngularModule,
  Mail,
  Map,
  MapPin,
  Maximize,
  Maximize2,
  Megaphone,
  Menu,
  Minimize,
  Minus,
  Moon,
  Mountain,
  Move,
  MoveLeft,
  MoveRight,
  Navigation,
  OctagonAlert,
  Option,
  Package,
  PaintBucket,
  PencilLine,
  Phone,
  PhoneOutgoing,
  Pin,
  Play,
  Plus,
  Power,
  PowerOff,
  RefreshCcw,
  SatelliteDish,
  Scissors,
  Settings,
  Settings2,
  Share2,
  SlidersVertical,
  Smartphone,
  SmartphoneNfc,
  Space,
  SquareCheckBig,
  SquarePen,
  SquareX,
  Star,
  StickyNote,
  Store,
  Sun,
  CloudFog,
  CloudRainWind,
  Snowflake,
  CloudSunRain,
  Target,
  Thermometer,
  ThermometerSun,
  TimerReset,
  TrafficCone,
  Trash2,
  TriangleAlert,
  Truck,
  User,
  User2,
  UserCog,
  UserRound,
  Users,
  UsersRound,
  Video,
  VideoOff,
  FileVideo2,
  Volume2,
  Wrench,
  X,
  ConciergeBell,
  Pencil,
  Send,
  ThumbsUp,
  ThumbsDown,
} from 'lucide-angular';
import { provideLottieOptions } from 'ngx-lottie';
import player from 'lottie-web';
import { BirdsViewService } from '@services/sockets/birds-view.service';
import { BirdsViewRxjsService } from '@services/sockets/birds-view-rxjs.service';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { registerLocaleData } from '@angular/common';
import localeAr from '@angular/common/locales/ar';
import localeFr from '@angular/common/locales/fr';
import localeEs from '@angular/common/locales/es';
import { translationUrl } from '@env/environment';
import { provideOwlConfig } from '@shared/providers/provide-owl-date-time';
import { provideNativeDateConfig } from '@shared/providers/provide-native-date-time';
import { NgxEchartsModule } from 'ngx-echarts';
import * as echarts from 'echarts/core';
import { LineChart, ScatterChart } from 'echarts/charts';
import {
  DataZoomComponent,
  GraphicComponent,
  GridComponent,
  MarkAreaComponent,
  TimelineComponent,
  TitleComponent,
  ToolboxComponent,
  TooltipComponent,
  VisualMapComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { SvgHolderComponent } from '@shared/components/svg-holder/svg-holder.component';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { getMessaging, provideMessaging } from '@angular/fire/messaging';
import { getStorage, provideStorage } from '@angular/fire/storage';
import { GlobalFilterInterceptor } from '@services/interceptors/global-filter.interceptor';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, `${translationUrl}/assets/i18n/`, '.json');
}

echarts.use([
  GridComponent,
  CanvasRenderer,
  TitleComponent,
  TooltipComponent,
  DataZoomComponent,
  GraphicComponent,
  LineChart,
  ScatterChart,
  ToolboxComponent,
  VisualMapComponent,
  MarkAreaComponent,
  TimelineComponent,
  GraphicComponent,
]);

const TooltipOptions: MatTooltipDefaultOptions = {
  showDelay: 0,
  hideDelay: 0,
  touchGestures: 'auto',
  position: 'below',
  touchendHideDelay: 0,
  disableTooltipInteractivity: true,
};

const providers = [
  {
    provide: ThemeService,
    useClass: ThemeService,
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: JwtInterceptor,
    multi: true,
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: GlobalFilterInterceptor,
    multi: true,
  },
  {
    provide: ErrorHandler,
    useClass: GlobalErrorHandler,
  },
  {
    provide: MAT_DIALOG_DEFAULT_OPTIONS,
    useValue: {
      disableClose: true,
      hasBackdrop: true,
      autoFocus: false,
      data: {},
    },
  },
  {
    provide: OverlayContainer,
    useClass: FullscreenOverlayContainer,
  },
  {
    provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
    useValue: TooltipOptions,
  },
  {
    provide: MAT_SELECT_CONFIG,
    useValue: {
      hideSingleSelectionIndicator: true,
    },
  },
  {
    provide: BirdsViewService,
    useClass: BirdsViewRxjsService,
  },
  provideLottieOptions({
    player: () => player,
  }),
  // { provide: LOCALE_ID, useValue: 'ar' },
  // { provide: LOCALE_ID, useValue: 'es' },
  // { provide: LOCALE_ID, useValue: 'fr' },
  // { provide: LOCALE_ID, useValue: 'en' }
  {
    provide: LOCALE_ID,
    useFactory: () => {
      return localStorage.getItem('appLocale') || 'en-US';
    },
  },
  provideNativeDateConfig(),
  provideOwlConfig(),
  provideFirebaseApp(() => initializeApp(firebaseConfig)),
  provideAuth(() => getAuth()),
  provideFirestore(() => getFirestore()),
  provideMessaging(() => getMessaging()),
  provideStorage(() => getStorage()),
];

@NgModule({
  declarations: [AppComponent],
  imports: [
    CoreModule,
    SharedModule,
    AppRoutingModule,
    MatButtonModule,
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    LoaderModule,
    MapModule,
    // AngularFireAuthModule,
    // AngularFireModule.initializeApp(firebaseConfig),
    // AngularFirestoreModule,
    // AngularFireMessagingModule,
    LucideAngularModule.pick({
      CirclePause,
      UserRound,
      Calendar,
      ClipboardCheck,
      FileText,
      Fuel,
      Handshake,
      Headphones,
      Home,
      Map,
      Megaphone,
      Package,
      Settings,
      Settings2,
      Target,
      Truck,
      User,
      Video,
      VideoOff,
      FileVideo2,
      Wrench,
      Bell,
      Plus,
      Menu,
      Layers,
      Pin,
      SlidersVertical,
      TrafficCone,
      SatelliteDish,
      Mountain,
      RefreshCcw,
      Download,
      Pencil,
      PencilLine,
      Trash2,
      Ellipsis,
      EllipsisVertical,
      Info,
      FileDown,
      CircleCheckBig,
      Columns2,
      Flag,
      Eye,
      EyeOff,
      CircleX,
      Copy,
      Volume2,
      CircleAlert,
      CircleCheck,
      Lock,
      PhoneOutgoing,
      Hourglass,
      StickyNote,
      Share2,
      Star,
      Maximize2,
      CircleStop,
      Sun,
      CloudFog,
      CloudRainWind,
      Snowflake,
      CloudSunRain,
      Store,
      Coffee,
      CornerDownRight,
      Check,
      MapPin,
      Space,
      Archive,
      Caravan,
      Delete,
      Image,
      SquareX,
      Clock9,
      ClockFading,
      Navigation,
      X,
      Phone,
      Clock2,
      BatteryCharging,
      Locate,
      LocateFixed,
      SmartphoneNfc,
      Maximize,
      Minimize,
      File,
      SquarePen,
      Scissors,
      Clock,
      Activity,
      UsersRound,
      Mail,
      SquareCheckBig,
      CirclePlus,
      ArrowUpRight,
      Play,
      TriangleAlert,
      OctagonAlert,
      Moon,
      Cigarette,
      Smartphone,
      Gauge,
      FilePlus,
      Users,
      Thermometer,
      ThermometerSun,
      DoorOpen,
      Move,
      MoveLeft,
      MoveRight,
      Droplet,
      Power,
      PowerOff,
      TimerReset,
      Book,
      LogOut,
      Filter,
      ChevronsUpDown,
      ChevronLeft,
      ChevronRight,
      HelpCircle,
      PaintBucket,
      UserCog,
      Option,
      LifeBuoy,
      Minus,
      User2,
      ConciergeBell,
      Send,
      ThumbsUp,
      ThumbsDown,
    }),
    NgxEchartsModule.forRoot({ echarts }),
    SvgHolderComponent,
  ],
  providers,
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {
  constructor() {
    registerLocaleData(localeAr);
    registerLocaleData(localeFr);
    registerLocaleData(localeEs);
  }
}
